import React, { useState } from 'react';
import { NavLink } from 'react-router-dom';
import { LayoutDashboard, Users, Mail, LayoutTemplate, FolderOpen, ShieldCheck, Settings, LogOut, Menu, X } from 'lucide-react';
import { useAdmin, useGroup } from '../context/AuthContext';
import { Logo } from './ui';

const links = [
  { to: '/admin', icon: LayoutDashboard, label: 'Dashboard', end: true },
  { to: '/admin/members', icon: Users, label: 'Members' },
  { to: '/admin/notifications', icon: Mail, label: 'Notifications' },
  { to: '/admin/layouts', icon: LayoutTemplate, label: 'Email layouts' },
  { to: '/admin/documents', icon: FolderOpen, label: 'Group documents' },
  { to: '/admin/admins', icon: ShieldCheck, label: 'Admins' },
  { to: '/admin/settings', icon: Settings, label: 'Settings' },
];

export default function AdminLayout({ children }) {
  const { admin, logout } = useAdmin();
  const { group } = useGroup();
  const [open, setOpen] = useState(false);

  const nav = (
    <nav className="flex h-full flex-col">
      <div className="flex items-center gap-3 border-b border-navy-700 p-4">
        <Logo size={44} />
        <div className="min-w-0">
          <p className="font-display text-lg font-bold leading-tight text-gold-400">{group?.shortName || 'RC 2008 Batch'}</p>
          <p className="text-xs text-navy-200">Admin panel</p>
        </div>
      </div>
      <div className="flex-1 overflow-y-auto py-3">
        {links.map(({ to, icon: Icon, label, end }) => (
          <NavLink key={to} to={to} end={end} onClick={() => setOpen(false)}
            className={({ isActive }) => `mx-2 my-0.5 flex items-center gap-3 rounded-lg px-3 py-2.5 text-sm transition-colors ${
              isActive ? 'bg-gold-500 font-semibold text-navy-900' : 'text-navy-100 hover:bg-navy-700 hover:text-white'}`}>
            <Icon size={18} />{label}
          </NavLink>
        ))}
      </div>
      <div className="border-t border-navy-700 p-4">
        <p className="truncate text-sm text-white">{admin?.name}</p>
        <p className="mb-3 truncate text-xs text-navy-300">{admin?.email} · {admin?.role === 'OWNER' ? 'Owner' : 'Admin'}</p>
        <button onClick={logout} className="flex w-full items-center gap-2 text-sm text-navy-200 hover:text-white">
          <LogOut size={16} /> Log out
        </button>
      </div>
    </nav>
  );

  return (
    <div className="flex min-h-screen bg-[#F4F6FA]">
      <div className="fixed inset-x-0 top-0 z-30 flex h-14 items-center gap-3 bg-navy-900 px-4 md:hidden">
        <button onClick={() => setOpen(true)} className="text-white" aria-label="Open menu"><Menu size={22} /></button>
        <span className="font-display text-lg font-bold text-gold-400">{group?.shortName || 'Admin'}</span>
      </div>
      {open && <div className="fixed inset-0 z-40 bg-black/50 md:hidden" onClick={() => setOpen(false)} />}
      <div className={`fixed inset-y-0 left-0 z-50 w-64 transform bg-navy-900 transition-transform md:hidden ${open ? 'translate-x-0' : '-translate-x-full'}`}>
        <button onClick={() => setOpen(false)} className="absolute right-3 top-4 text-navy-200" aria-label="Close menu"><X size={20} /></button>
        {nav}
      </div>
      <aside className="sticky top-0 hidden h-screen w-60 flex-shrink-0 bg-navy-900 md:block">{nav}</aside>
      <main className="mt-14 min-w-0 flex-1 p-4 md:mt-0 md:p-8">{children}</main>
    </div>
  );
}
